// Pricing page with a single "Buy" button that creates a Checkout session
// and redirects to the Stripe-hosted page. Distinct loading/error/unconfigured
// states mirror the HistoryView pattern so the UX is predictable.
import { useState } from 'react';
import { startCheckout } from '../lib/billing.js';

export default function Pricing({ onBack, onPostCheckout }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unconfigured, setUnconfigured] = useState(false);

  async function buy() {
    setError(null);
    setUnconfigured(false);
    setBusy(true);
    try {
      const session = await startCheckout({});
      // Redirect to Stripe Checkout. The success_url returns to /?checkout=success&session_id=...
      window.location.href = session.url;
    } catch (err) {
      if (err.status === 503) setUnconfigured(true);
      else setError(err?.message || 'Checkout failed');
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pricing</h1>
          <p className="text-sm text-slate-400">
            Pay-as-you-go credit pack. One credit = one full file review with PDF + DOCX export.
          </p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-md border border-navy-700 bg-navy-800 px-4 py-2 text-sm hover:border-gold"
          >
            Back
          </button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gold/40 bg-navy-800 p-6 shadow-lg">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gold">
            Starter pack
          </div>
          <div className="mb-2 text-3xl font-bold text-white">10 reviews</div>
          <div className="mb-4 text-sm text-slate-300">
            Best for occasional remediation work.
          </div>
          <div className="mb-6 text-2xl font-semibold text-white">
            £49 <span className="text-sm font-normal text-slate-400">/ pack</span>
          </div>
          <ul className="mb-6 space-y-2 text-sm text-slate-200">
            <li>• Full 6-module pipeline per review</li>
            <li>• PDF + Word regulator-ready report</li>
            <li>• Persistent review history (Notion)</li>
            <li>• 90-day credit expiry</li>
          </ul>
          <button
            type="button"
            onClick={buy}
            disabled={busy}
            className="w-full rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Opening Stripe…' : 'Buy 10 reviews'}
          </button>
        </div>

        <div className="rounded-lg border border-navy-700 bg-navy-800 p-6">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Volume / annual
          </div>
          <div className="mb-2 text-3xl font-bold text-white">Talk to us</div>
          <div className="mb-4 text-sm text-slate-300">
            For firms running ongoing remediation programmes or RegTech integration.
          </div>
          <ul className="mb-6 space-y-2 text-sm text-slate-200">
            <li>• Volume pricing ladders</li>
            <li>• Per-user accounts (replaces shared password)</li>
            <li>• Custom prompt tuning to your firm's QA framework</li>
            <li>• Data Processing Addendum + SOC2-equivalent posture</li>
          </ul>
          <a
            href="mailto:hello@cognitive-compliance.co.uk?subject=RegDefence%20AI%20–%20enterprise%20enquiry"
            className="block w-full rounded-md border border-gold px-4 py-2 text-center text-sm font-semibold text-gold transition hover:bg-gold hover:text-navy-900"
          >
            Email us
          </a>
        </div>
      </div>

      {unconfigured && (
        <div className="space-y-2 rounded-lg border border-amber-900/40 bg-amber-900/10 p-5 text-sm text-amber-100">
          <div className="font-semibold">Billing is not configured.</div>
          <p className="text-amber-200">
            To enable real Checkout, set <code className="rounded bg-navy-900 px-1 py-0.5">STRIPE_SECRET_KEY</code>,{' '}
            <code className="rounded bg-navy-900 px-1 py-0.5">STRIPE_PRICE_ID</code>, and{' '}
            <code className="rounded bg-navy-900 px-1 py-0.5">STRIPE_WEBHOOK_SECRET</code> in
            the environment. Use{' '}
            <code className="rounded bg-navy-900 px-1 py-0.5">sk_test_*</code> keys for
            test-mode validation.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-900/50 bg-red-900/20 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      <p className="text-xs text-slate-500">
        Stripe handles payment processing. Card details never touch RegDefence servers.
        See <a className="underline" href="https://stripe.com/docs/security">stripe.com/docs/security</a>.
      </p>
      {onPostCheckout && null /* reserved for the success-toast wiring in App.jsx */}
    </div>
  );
}
